<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset}" ref="observer">
    <artisio-modal :visible="modal.show" :data="object" :title="computedTitle" :content-in-form="false"
                   :show-confirmation="showConfirmation" :is-loading="loading" scrollable size="lg"
                   modal-class="mobile-full-screen" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)">
      <div class="row mb-2" v-if="computedInvoice">

        <div class="col-md-6">

          <div class="row">
            <div class="col-sm-3"><b>{{ $t('Invoice') }}:</b></div>
            <div class="col-sm-6">
              <a href="javascript:void(0)" @click="onInvoiceClick(computedInvoice)">
                {{ computedInvoice.invoice_no }}
              </a>
            </div>
          </div>

          <div class="row" v-if="isUpdate">
            <div class="col-sm-3">
              <b>{{ $t('Credit Note') }}:</b>
            </div>
            <div class="col-sm-6">
              <a href="javascript:void(0)" @click="onInvoiceClick(object)">
                {{ object.invoice_no }}
              </a>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12 mb-2 mt-2" v-if="computedInvoice.customer">
              <customer-email-address-info :customer="computedInvoice.customer"/>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>


        <div class="col-md-12">
          <table class="info-table table table-sm invoice-payments-table ">
            <tbody>
            <tr>
              <th class="border-top-0">{{ $t('Stock No') }}</th>
              <th class="border-top-0" v-if="isAuctionType">{{ $t('Lot No') }}</th>
              <th class="border-top-0">{{ $t('Title') }}</th>
              <th class="border-top-0">{{ $t('Total') }}</th>
            </tr>
            <tr v-for="financial_item in computedFinancialItems" :key="financial_item.uuid">
              <td>
                <router-link
                  v-if="$hasPermission('view_receipt')" target="_blank"
                  :to="{name: 'receipt.view', params: {id: getInventory(financial_item)['receipt_uuid']}}">
                  {{ getPartialStockNo(financial_item, 0) }}
                </router-link>
                <span v-else>{{ getPartialStockNo(financial_item, 0) }}</span>
                /
                <router-link
                  v-if="$hasPermission('view_inventoryitem')" target="_blank"
                  :to="{ name: 'inventory.view', params: { id: getInventory(financial_item)['uuid'] }}">
                  {{ getPartialStockNo(financial_item, 1) }}
                </router-link>
                <span v-else>{{ getPartialStockNo(financial_item, 1) }}</span>
              </td>
              <td v-if="isAuctionType">
                <router-link
                  v-if="$hasPermission('view_inventoryitem')" target="_blank"
                  :to="{ name: 'reservations.view', params: { id: financial_item.lot.reservation_uuid }}">
                  {{ financial_item.lot.lot_no }}
                </router-link>
                <span v-else>{{ financial_item.lot.lot_no }}</span>
              </td>
              <td v-html="getInventory(financial_item)['short_description']"/>
              <td>{{ financial_item.buyer_total | asCurrency(computedInvoice.currency.symbol) }}</td>
            </tr>
            <tr v-if="computedInvoice.currency">
              <td :colspan="computedColspan" class="text-right border-top-0 pb-0">
                <span class="d-inline-block mr-3"><b>{{ $t('Total Amount') }}</b></span>
                <span>
                  {{ computedInvoice.total_amount | asNegativeNumber | asCurrency(computedInvoice.currency.symbol) }}
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <hr class="mt-2">
      <input-widget :model="model" attribute="comment" type="textarea"/>
      <template v-slot:modal-footer v-if="enableCreditNoteWithoutRefund && !isUpdate">
        <cancel-button @click="onHidden">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="onSubmit">
          {{ $t('Submit')}}
        </submit-button>
        <submit-button @click="onSubmitIssue">
          {{ $t('Submit and Issue New Invoice')}}
        </submit-button>
      </template>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import CreditNoteModel from "./models/CreditNote.model";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {
    INVOICE_TYPE_AUCTION,
    INVOICE_TYPE_MANUAL,
    INVOICE_TYPE_DEPOSIT,
    INVOICE_TYPE_SERVICE,
    SOURCE_INVOICES_TABLE
  } from "../constants";
  import CancelButton from "@/core/components/common/CancelButton.vue";
  import SubmitButton from "@/core/components/common/SubmitButton.vue";
  import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo";
  import InvoicesService from "@/modules/invoices/InvoicesService";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "CreditNoteModal",
    components: {CustomerEmailAddressInfo, InputWidget, ArtisioModal, CancelButton, SubmitButton},

    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new CreditNoteModel(),
      }
    },
    computed: {
      ...mapState({
        modal: state => state.creditNoteModal,
        object: state => state.creditNoteModal.object,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        enableCreditNoteWithoutRefund: state => state.enableCreditNoteWithoutRefund
      }),
      computedTitle() {
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Create Credit Note')
        }
        return this.$i18n.t('Update Credit Note {no}', {no: this.model.invoice_no})
      },
      computedInvoice() {
        return this.isUpdate ? this.object.credited_invoice : this.object;
      },
      computedFinancialItems() {
        return this.isUpdate ? this.computedInvoice.credited_financial_items : this.computedInvoice.financial_items;
      },
      computedColspan() {
        return this.isAuctionType ? 4 : 3;
      },
      isUpdate() {
        return this.object && this.object.is_credit_note;
      },
      isAuctionType() {
        return this.computedInvoice.type === INVOICE_TYPE_AUCTION;
      },
      isManualType() {
        return this.computedInvoice.type === INVOICE_TYPE_MANUAL;
      },
    },
    watch: {
      object() {
        if (this.isUpdate) {
          this.model = new CreditNoteModel(this.object);
        }
      },
    },
    methods: {
      ...mapActions([
        'hideCreditNoteModal',
        'createCreditNote',
        'updateCreditNote',
        'showContinuousAuctionInvoiceModal',
        'showModal',
        'getModalObject',
        'showManualInvoiceModal',
        'getManualInvoiceModalObject',
        'showDepositInvoiceModal',
        'showServiceInvoiceModal',
        'getServiceInvoiceModalObject'
      ]),
      onHidden() {
        this.model = new CreditNoteModel();
        this.hideCreditNoteModal();
      },
      async onInvoiceClick(invoice) {
        await InvoicesService.viewInvoicePdf(this, invoice, true)
      },
      getPartialStockNo(item, index) {
        return this.getInventory(item)['stock_no'].split('/')[index];
      },
      getInventory(item) {
        if (this.isAuctionType) {
          return item.lot.reservation.inventory;
        } else if (this.isManualType) {
          return item.inventory;
        }
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }
        this.loading = true;
        let params = {
          date: this.model.date,
          invoice_uuid: this.object.uuid,
          comment: this.model.comment,
        };
        let response
        if (this.isUpdate) {
          response = await this.updateCreditNote(params);
        } else {
          response = await this.createCreditNote(params);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.onHidden();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          }
        }
      },
      async onSubmitIssue() {
        let success = await this.$confirm(
            null,
            null,
            null,
            null,
            this.$i18n.t('Please Confirm'),
            this.$i18n.t('Yes'),
            this.$i18n.t('No'),
            null,
            {
              label: this.$i18n.t("Please type 'confirm' to proceed"),
              text: 'confirm'
            }
        )
        if (!success) {
          return;
        }

        let creditNoteParams = {
          comment: this.model.comment,
          date: this.model.date
        };

        if (this.object.type === INVOICE_TYPE_AUCTION) {
          if (this.enableContinuousAuction) {
            this.showContinuousAuctionInvoiceModal({
              source: SOURCE_INVOICES_TABLE,
              sale: this.object.auction,
              creditNoteWithoutRefundParams: creditNoteParams
            });
          } else {
            this.showModal({
              source: SOURCE_INVOICES_TABLE,
              sale: this.sale ? this.sale : this.object.auction,
              creditNoteWithoutRefundParams: creditNoteParams
            });
          }
          this.getModalObject({uuid: this.object.uuid, removeOldData: true});
        } else if (this.object.type === INVOICE_TYPE_MANUAL) {
          this.showManualInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            customer: this.object.customer,
            creditNoteWithoutRefundParams: creditNoteParams
          })
          this.getManualInvoiceModalObject({uuid: this.object.uuid, removeOldData: true});
        } else if (this.object.type === INVOICE_TYPE_DEPOSIT) {
          this.showDepositInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            object: this.object,
            customer: this.object.customer,
            creditNoteWithoutRefundParams: creditNoteParams
          })
        } else if (this.object.type === INVOICE_TYPE_SERVICE) {
          this.showServiceInvoiceModal({
            source: SOURCE_INVOICES_TABLE,
            customer: this.object.customer,
            creditNoteWithoutRefundParams: creditNoteParams
          })
          this.getServiceInvoiceModalObject({uuid: this.object.uuid, removeOldData: true});
        }
        this.onHidden();
      }
    },
  }
</script>

<style scoped>

</style>
